import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation } from "react-query";
import { formatCurrency } from "../../utils/helpers";
import api from "../../utils/api";

/**
 * Payment Checkout Page
 * Integrate your payment gateway (Razorpay, Stripe, etc.) here.
 * The booking/order ID is passed as a query param.
 */
export default function PaymentCheckout() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const orderId = searchParams.get("orderId");
  const [paymentMethod, setPaymentMethod] = useState("card");

  const { data: orderData, isLoading } = useQuery(
    ["payment-order", orderId],
    () => api.get(`/payment/order/${orderId}`),
    { enabled: !!orderId }
  );

  const payMutation = useMutation(
    () => api.post("/payment/initiate", { orderId, paymentMethod }),
    {
      onSuccess: (res) => {
        if (res.gatewayUrl) {
          window.location.href = res.gatewayUrl;
        } else {
          navigate(`/payment/success?orderId=${orderId}`);
        }
      },
      onError: () => navigate(`/payment/failed?orderId=${orderId}`),
    }
  );

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const order = orderData?.order;

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-teal-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <h1 className="font-display font-bold text-2xl text-primary-700">🏥 HealthConnect</h1>
          <p className="text-slate-500 text-sm mt-1">Secure Payment</p>
        </div>

        <div className="card space-y-5">
          {/* Order Summary */}
          <div className="bg-slate-50 rounded-xl p-4 space-y-2">
            <h3 className="font-display font-semibold text-slate-700 text-sm uppercase tracking-wide">
              Order Summary
            </h3>
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">{order?.description || "Medical Service"}</span>
              <span className="font-semibold text-slate-800">
                {formatCurrency(order?.amount)}
              </span>
            </div>
            {order?.taxAmount > 0 && (
              <div className="flex justify-between text-sm text-slate-500">
                <span>Tax</span>
                <span>{formatCurrency(order.taxAmount)}</span>
              </div>
            )}
            <div className="flex justify-between font-semibold border-t border-slate-200 pt-2">
              <span>Total</span>
              <span className="text-primary-600 text-lg">
                {formatCurrency((order?.amount || 0) + (order?.taxAmount || 0))}
              </span>
            </div>
          </div>

          {/* Payment Methods */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-3 uppercase tracking-wide">
              Payment Method
            </label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { key: "card", label: "Card", icon: "💳" },
                { key: "upi", label: "UPI", icon: "📱" },
                { key: "netbanking", label: "Net Banking", icon: "🏦" },
              ].map((method) => (
                <button
                  key={method.key}
                  onClick={() => setPaymentMethod(method.key)}
                  className={`flex flex-col items-center gap-1 p-3 rounded-xl border text-sm font-medium transition-colors ${
                    paymentMethod === method.key
                      ? "bg-primary-600 border-primary-600 text-white"
                      : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  <span>{method.icon}</span>
                  {method.label}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={() => payMutation.mutate()}
            disabled={payMutation.isLoading}
            className="btn-primary w-full text-base py-3"
          >
            {payMutation.isLoading
              ? "Processing…"
              : `Pay ${formatCurrency(order?.amount)}`}
          </button>

          <p className="text-center text-xs text-slate-400">
            🔒 256-bit SSL encrypted. Your payment is secure.
          </p>
        </div>
      </div>
    </div>
  );
}
